// frontend/src/pages/GeneratedFilesPage.jsx — 내가 만든 파일(AI 생성 문서) 회수 목록.
//
// 🔴 왜 있는가: 파일 생성은 5~10분 걸린다. 그동안 브라우저를 닫거나 네트워크가 끊기면
//    서버는 파일을 끝까지 만들어 저장하는데 **다운로드 카드를 받을 방법이 없었다**
//    (그 응답이 저장되기 전이라 대화 본문에도 안 남는다). 여기서 되찾는다.
import { useState, useEffect, useCallback } from 'react';
import { IconFileDownload, IconFile, IconFileTypography, IconDatabase, IconClock } from '@tabler/icons-react';
import { chatAPI } from '../api/client';
import AppShell from '../components/AppShell';
import { Page, PageHeader, Button, SearchInput, DataTable } from '../components/ui';
import { COLORS, ICON_STROKE } from '../utils/uiStyles';
import { alertDialog } from '../components/AppDialog';

const PAGE_SIZE = 20;

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.endsWith('Z') ? s : `${s}Z`);
  return d.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const fmtSize = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.ceil(n / 1024).toLocaleString()} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function GeneratedFilesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [term, setTerm] = useState('');        // 입력 중인 값
  const [query, setQuery] = useState('');      // 실제 조회에 쓰는 값
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await chatAPI.listGenerated({ page, size: PAGE_SIZE, q: query });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  const download = async (f) => {
    if (!f.available) {
      alertDialog('이 파일은 서버에 없습니다(삭제되었거나 보관 기간이 지났습니다).');
      return;
    }
    setBusy(f.id);
    try {
      const res = await chatAPI.downloadGenerated(f.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alertDialog(e?.response?.status === 404
        ? '파일을 찾을 수 없습니다.'
        : '다운로드에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const search = (e) => { e.preventDefault(); setPage(1); setQuery(term.trim()); };

  return (
    // 🔴 `<AppSidebar>{내용}</AppSidebar>` 로 감싸면 안 된다 — AppSidebar 의 children 은
    //    **사이드바 안 아코디언**(대화 목록·폴더 목록) 자리다. 페이지 전체를 거기 넣으면
    //    본문이 사이드바 폭(약 500px) 안에 렌더되고 오른쪽은 빈 화면이 된다(실제로 그랬다).
    //    다른 페이지처럼 **사이드바와 형제**로 두고 본문이 남은 폭을 차지하게 한다.
    <AppShell app="generated">
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: 'var(--gw-bg, #fff)' }}>
      <Page>
        <PageHeader
          icon={IconFileDownload}
          tone="red"
          title="만든 파일"
          subtitle="AI 채팅에서 만든 문서입니다. 생성 도중 창을 닫아 다운로드를 못 받았어도 여기서 받을 수 있습니다."
        />

        <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <SearchInput value={term} onChange={(e) => setTerm(e.target.value)} placeholder="파일명 검색" width={320} />
          <Button type="submit" variant="secondary">검색</Button>
          {query && (
            <Button
              variant="quiet"
              onClick={() => { setTerm(''); setQuery(''); setPage(1); }}
            >초기화</Button>
          )}
        </form>

        {error && <div style={{ color: COLORS.dangerText, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {/* 검색·페이지 이동으로 열 너비가 요동치지 않게 고정폭 — 칸은 말줄임(DataTable)이라 형식·크기·시각·단추 열은 px 로 준다.
            ≤600 은 stackOnMobile 이 행을 블록으로 쌓는다(minWidth 는 데스크톱에서만 산다). */}
        <DataTable
          columns={[
            { key: 'filename', label: '파일명', icon: IconFile, render: (f) => (
              <span title={f.filename} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontWeight: 500 }}>
                <IconFile size={16} stroke={ICON_STROKE} aria-hidden="true" style={{ color: 'var(--gw-muted, #92918d)', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
                {!f.available && <span style={{ color: 'var(--gw-muted, #92918d)', fontWeight: 400, flexShrink: 0 }}>(없음)</span>}
              </span>
            ) },
            { key: 'format', label: '형식', icon: IconFileTypography, width: '72px', render: (f) => (f.format || '').toUpperCase() },
            { key: 'size', label: '크기', icon: IconDatabase, width: '88px', render: (f) => fmtSize(f.size) },
            { key: 'created_at', label: '만든 시각', icon: IconClock, width: '170px', render: (f) => fmtDate(f.created_at) },
            { key: 'action', label: '', width: '100px', render: (f) => (
              <Button
                variant="secondary" size="sm"
                onClick={() => download(f)}
                disabled={!f.available || busy === f.id}
              >{busy === f.id ? '받는 중…' : '다운로드'}</Button>
            ) },
          ]}
          rows={loading ? [] : items}
          rowKey="id"
          empty={loading ? '불러오는 중…' : (query ? '검색 결과가 없습니다.' : '아직 만든 파일이 없습니다.')}
          minWidth={720}
          stackOnMobile
        />

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13 }}>
            <Button variant="secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>처음</Button>
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>이전</Button>
            <span style={{ color: COLORS.textMuted, margin: '0 4px' }}>
              {page} / {pages} 쪽 · 전체 {total.toLocaleString()}개
            </span>
            <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>다음</Button>
            <Button variant="secondary" size="sm" onClick={() => setPage(pages)} disabled={page >= pages}>마지막</Button>
          </div>
        )}
      </Page>
      </div>
    </AppShell>
  );
}
